import React, { useState, useEffect,useRef } from 'react';
import logo from './assets/mqoelogo.png';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, BookOpen, GraduationCap, Users, Heart, Star, BookCheck } from 'lucide-react';


// Animation variants for Framer Motion (concept implemented via CSS for zero-dependency portability)
const CourseCard = ({ title, description, features, icon: Icon, delay,onContactClick }) => (
  <div 
    className="glass-card group relative rounded-3xl p-8 shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full border border-white/20 overflow-hidden"
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Glowing background effect on hover */}
    <div className="absolute -inset-24 bg-gradient-to-r from-[#147a44]/20 to-[#a6ff00]/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

    <div className="relative z-10 flex flex-col items-center mb-6 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-white to-[#f0f9f4] rounded-2xl flex items-center justify-center mb-4 shadow-inner group-hover:rotate-[360deg] transition-transform duration-700">
        <Icon className="w-10 h-10 text-[#147a44] drop-shadow-md" />
      </div>
      <h3 className="text-2xl font-bold text-[#1a3a2a] group-hover:text-[#147a44] transition-colors">{title}</h3>
    </div>
    
    <p className="relative z-10 text-gray-700 text-sm leading-relaxed mb-6 text-center font-medium">
      {description}
    </p>
    
    <ul className="relative z-10 space-y-4 mb-8 flex-grow">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-3 text-sm font-semibold text-[#2d4d3d]">
          {/* Replaced Green Ticks with Golden Moon */}
          <span className="text-[#d4af37] text-xs filter drop-shadow-[0_0_5px_rgba(212,175,55,0.4)]">🌙</span>
          {feature}
        </li>
      ))}
    </ul>
    
    <button onClick={onContactClick}
     className="relative z-10 w-full bg-gradient-to-r from-[#147a44] to-[#1e9e5a] text-white py-4 rounded-2xl font-bold text-lg shadow-[0_10px_20px_rgba(20,122,68,0.3)] hover:shadow-[0_15px_30px_rgba(20,122,68,0.5)] active:scale-95 transition-all">
      Get Free Trial
    </button>
  </div>
);

export default function All() {

   const navigate = useNavigate(); // ✅ Hook at top level

  const handleContactClick = () => {
    if (window.location.pathname === "/") {
      // Already on home page, scroll smoothly
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    } else {
      // Navigate to home page + hash
      navigate("/#contact");
    }
  };
 

     const [isMenuOpen, setIsMenuOpen] = useState(false);
      const [isServicesOpen, setIsServicesOpen] = useState(false);
      const [formSubmitted, setFormSubmitted] = useState(false);
      const [userName, setUserName] = useState('');
    
      useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css';
        document.head.appendChild(link);
      }, []);
    
      const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    
      const handleSubmit = (e) => {
        e.preventDefault();
        const name = e.target.elements.name.value;
        setUserName(name);
        setFormSubmitted(true);
      };
    
      const serviceOptions = [
       { name: "Noorani Qaida", link: "/noorani" },
    { name: "Tajweed-e-Quran", link: "/tajweed" },
    { name: "Basic Islamic Educations", link: "/all" }
      ];

  const courses = [
    { title: "Quran Reading & Recitation", icon: BookOpen, description: "Building strong reading skills starts at the ground level. Beginners start with Noorani Qaida to grasp Arabic letters.", features: ["Step-by-step Noorani Qaida", "Short daily practice", "Full page recitation", "Family support"] },
    { title: "Islamic Studies", icon: GraduationCap, description: "Covers core beliefs, Fiqh, Seerah, and manners. Lessons connect with Quranic learning for all ages.", features: ["Fiqh & daily rules", "Seerah and historical context", "Dua & manners", "Quranic roots"] },
    { title: "Personalized Classes", icon: Users, description: "Choose 1:1 attention or small groups. Sessions adapt to your pace and encourage shared learning.", features: ["1:1 Private attention", "Small group interaction", "Self-paced progress", "Flexible options"] },
    {
  title: "Noorani Qaida Learning",
  icon: BookOpen,
  description: "Learn Quran reading from the basics with proper pronunciation (Tajweed). Step-by-step guidance makes it easy for beginners and kids.",
  features: [
    "Basic Arabic letters recognition",
    "Proper Tajweed rules",
    "Step-by-step learning",
    "Beginner friendly approach"
  ]
}
  

];

  return (
    <div className="min-h-screen bg-[#ece0d1] relative overflow-hidden font-sans pb-20">
     {/* CSS Styles injected via Template Literal to maintain exactness */}
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
        
        :root {
            --primary-green: #147a44;
            --submit-brown: #634d3a;
            --accent-lime: #a6ff00;
        }

        body {
            font-family: 'Inter', sans-serif;
        }

        .nav-link {
            font-weight: 600;
            color: #1a3a2a;
            transition: color 0.3s;
            display: flex;
            align-items: center;
            gap: 4px;
            cursor: pointer;
        }

        .nav-link:hover {
            color: var(--primary-green);
        }

        .dropdown-menu {
            position: absolute;
            top: 100%;
            left: 0;
            background: white;
            min-width: 220px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            border-radius: 8px;
            padding: 10px 0;
            opacity: 0;
            visibility: hidden;
            transform: translateY(10px);
            transition: all 0.3s ease;
            border-top: 3px solid var(--primary-green);
        }

        .group:hover .dropdown-menu {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
        }

        .dropdown-item {
            padding: 10px 20px;
            display: block;
            color: #1a3a2a;
            font-weight: 500;
            transition: all 0.2s;
        }

        .dropdown-item:hover {
            background-color: #f4fbf7;
            color: var(--primary-green);
            padding-left: 25px;
        }

        .green-pill {
            background-color: #e9f5ee;
            color: var(--primary-green);
            padding: 8px 16px;
            border-radius: 25px;
            font-size: 14px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .course-card {
            background: white;
            border-radius: 20px;
            padding: 40px 30px;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0,0,0,0.05);
            transition: transform 0.3s ease;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .course-card:hover {
            transform: translateY(-10px);
        }

        .stat-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(12px);
            padding: 40px 20px;
            border-radius: 24px;
            text-align: center;
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
            transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            position: relative;
            overflow: hidden;
            box-shadow: 0 15px 35px rgba(0,0,0,0.2);
            transform-style: preserve-3d;
            perspective: 1000px;
        }

        .stat-card:hover {
            transform: translateY(-10px) rotateX(5deg) rotateY(5deg);
            border-color: var(--accent-lime);
            box-shadow: 0 25px 50px rgba(0,0,0,0.3), 0 0 20px rgba(166, 255, 0, 0.2);
        }

        .stat-card::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(166, 255, 0, 0.1) 0%, transparent 70%);
            opacity: 0;
            transition: opacity 0.3s;
        }

        .stat-card:hover::before {
            opacity: 1;
        }

        .form-input {
            width: 100%;
            border: 1px solid #ced4da;
            padding: 15px;
            border-radius: 4px;
            margin-bottom: 15px;
            outline: none;
            transition: border-color 0.3s;
        }

        .form-input:focus {
            border-color: var(--primary-green);
        }

        .btn-submit {
            background-color: var(--submit-brown);
            color: white;
            width: 100%;
            padding: 15px;
            border-radius: 4px;
            font-weight: 700;
            text-transform: capitalize;
            font-size: 18px;
            transition: opacity 0.3s;
            cursor: pointer;
        }

        .btn-submit:hover {
            opacity: 0.9;
        }

        .whatsapp-float {
            position: fixed;
            bottom: 30px;
            right: 30px;
            background-color: #25d366;
            color: white;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 30px;
            box-shadow: 2px 5px 15px rgba(0,0,0,0.3);
            z-index: 1000;
            transition: transform 0.3s;
        }

        .whatsapp-float:hover {
            transform: scale(1.1);
        }

        .stats-bg-glow {
            position: absolute;
            width: 300px;
            height: 300px;
            background: var(--accent-lime);
            filter: blur(150px);
            opacity: 0.15;
            border-radius: 50%;
            pointer-events: none;
        }
      `}</style>

           {/* Header */}
     <header className="py-4 px-6 lg:px-24 bg-white fixed top-0 left-0 right-0 z-[1000] shadow-md">
  <div className="max-w-7xl mx-auto flex justify-between items-center">
    <div className="flex items-center gap-3">
      {/* Logo Section */}
      <div className="flex items-center">
        <img 
          src={logo} 
          alt="Madina Quran Online Education" 
          className="h-16 w-auto object-contain"
        />
      </div>
      
      {/* Brand Name */}
      <span className="text-sm md:text-lg lg:text-xl font-extrabold text-[#147a44]">
        Madina Quran Online Education
      </span>
      {/* Social Media Icons (Desktop) */}
    <div className="hidden md:flex items-center gap-4 ml-6">
      <a href="https://www.facebook.com/people/Jamia-Madina-Tul-Quran-Online-Academy/61581687654772/?rdid=a5sb3u7e1r2ckVRz&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1G7EYvJyud%2F" className="text-green-600 hover:text-[#147a44] transition text-lg">
        <i className="fab fa-facebook-f"></i>
      </a>
      <a href="#" className="text-green-600 hover:text-[#147a44] transition text-lg">
        <i className="fab fa-instagram"></i>
      </a>
      <a href="https://wa.me/+923155141289" target="_blank" rel="noopener noreferrer"  className="text-green-600 hover:text-[#147a44] transition text-lg">
        <i className="fab fa-whatsapp"></i>
      </a>
    </div>
    </div>
    
    <nav className="hidden md:flex gap-10">
      <a href="/" className="nav-link">Home</a>
      
      {/* Desktop Dropdown */}
      <div className="relative group">
        <div className="nav-link cursor-pointer">
          Services <i className="fas fa-chevron-down text-[10px] ml-1"></i>
        </div>
        <div className="dropdown-menu">
          {serviceOptions.map((option, idx) => (
            <a key={idx} href={option.link} className="dropdown-item">{option.name}</a>
          ))}
        </div>
      </div>

      <a href="/#contact" className="nav-link">Contact Us</a>
    </nav>

    

    <div className="md:hidden text-2xl cursor-pointer" onClick={toggleMenu}>
      <i className={isMenuOpen ? "fas fa-times" : "fas fa-bars"}></i>
    </div>
  </div>
</header>

     
      {/* Mobile Nav */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden fixed top-[72px] left-0 right-0 bg-white p-5 flex flex-col gap-4 shadow-2xl z-[999] border-t"
          >
            <a href="#" className="nav-link py-3 border-b border-gray-100" onClick={() => setIsMenuOpen(false)}>Home</a>
            <div className="nav-link py-3 justify-between" onClick={() => setIsServicesOpen(!isServicesOpen)}>
                Services <i className={`fas fa-chevron-${isServicesOpen ? 'up' : 'down'} text-sm`}></i>
            </div>
            {isServicesOpen && (
                <div className="pl-4 flex flex-col space-y-3">
                    {serviceOptions.map((o, i) => <a key={i} href={o.link} className="text-sm font-medium text-gray-600">{o.name}</a>)}
                </div>
            )}
            <a href="#contact" className="nav-link py-3" onClick={() => setIsMenuOpen(false)}>Contact Us</a>

             {/* Social Media Icons (Mobile) */}
          <div className="flex gap-4 pt-3">
            <a href="https://www.facebook.com/people/Jamia-Madina-Tul-Quran-Online-Academy/61581687654772/?rdid=a5sb3u7e1r2ckVRz&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1G7EYvJyud%2F"><i className="fab fa-facebook-f text-gray-600"></i></a>
            <a href="#"><i className="fab fa-instagram text-gray-600"></i></a>
            <a href="#"><i className="fab fa-whatsapp text-gray-600"></i></a>
          </div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* Background Animated Blobs for Glassmorphism depth */}
      <div className="absolute  mt-50 left-[-10%] w-[50%] h-[50%] bg-[#a6ff00]/20 rounded-full blur-[120px] animate-pulse"></div>
      <div className="absolute bottom-[10%] right-[-10%] w-[40%] h-[40%] bg-[#147a44]/10 rounded-full blur-[120px] animate-bounce-slow"></div>

      <div className=" mt-40px relative z-10 max-w-7xl mx-auto px-4 pt-12">
        {/* Header Hero */}
        <div className="relative rounded-[50px] overflow-hidden shadow-2xl mb-20 group">
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-700 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1585036156171-384164a8c675?auto=format&fit=crop&q=80&w=2000" 
            alt="Holy Quran" 
            className="w-full h-[500px] object-cover scale-105 group-hover:scale-100 transition-transform duration-[2s]"
          />
          <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-center p-6">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 scale-up-center">
              <h1 className="text-4xl md:text-6xl font-black text-white mb-4 drop-shadow-2xl">
               Madinah Quran Online Education
              </h1>
              <p className="text-white/90 text-lg md:text-xl font-medium">Expert Quran Teaching with Tajweed Worldwide</p>
            </div>
          </div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-20 reveal">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#147a44] mb-6 flex items-center justify-center gap-4">
            <span className="animate-bounce">🌙</span> Our Premium Courses
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#147a44] to-[#a6ff00] mx-auto rounded-full shadow-lg"></div>
        </div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {courses.map((course, index) => (
            <CourseCard  onContactClick={handleContactClick} key={index} {...course} delay={index * 150} />
          ))}
        </div>
      </div>

      {/* Floating WhatsApp with Glow */}
      <a 
        href="https://wa.me/+923155141289" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-10 right-10 bg-[#25D366] text-white p-5 rounded-full shadow-[0_0_30px_rgba(37,211,102,0.6)] hover:shadow-[0_0_50px_rgba(37,211,102,0.8)] hover:scale-110 transition-all z-50 flex items-center justify-center animate-bounce-slow"
      >
        <MessageCircle size={36} fill="currentColor" />
      </a>
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
        
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          animation: slideUp 0.8s ease-out forwards;
          opacity: 0;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .animate-bounce-slow {
          animation: bounce-slow 4s infinite ease-in-out;
        }

        .scale-up-center {
          animation: scale-up-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
        }

        @keyframes scale-up-center {
          0% { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        /* Subtle moving background for blobs */
        @keyframes blob-move {
          0% { transform: translate(0, 0); }
          50% { transform: translate(30px, 50px); }
          100% { transform: translate(0, 0); }
        }
      `}</style>
    </div>
  );
}